/**
 * @file     ListingStickyBarAuto.tsx
 * @purpose  ListingStickyBar with its section anchors read from the page:
 *           every ListingSection that carries an anchor label (and the
 *           streamed similar-homes block) becomes a link. The sections are
 *           composed deep in the server body after the bar renders, so the
 *           bar collects them once mounted and again when streamed content
 *           lands. The bar only shows after the rail actions scroll away.
 */
'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SectionAnchorItem } from '@/components/hub/SectionAnchors';
import { ListingStickyBar } from './ListingStickyBar';
import type { ListingStickyBarListing } from './stickyBarListing';

interface Props {
  listing: ListingStickyBarListing;
  /** Id of the hero's rail actions; the bars show once it leaves the viewport. */
  anchorId: string;
}

function collect(): SectionAnchorItem[] {
  return Array.from(document.querySelectorAll<HTMLElement>('[data-anchor-label][id]'))
    .map((node) => ({ id: node.id, label: node.dataset.anchorLabel ?? '' }))
    .filter((item) => item.label.length > 0);
}

const sameItems = (a: SectionAnchorItem[], b: SectionAnchorItem[]) =>
  a.length === b.length && a.every((item, i) => item.id === b[i]?.id && item.label === b[i]?.label);

export function ListingStickyBarAuto({ listing, anchorId }: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<SectionAnchorItem[]>([]);

  useEffect(() => {
    const update = () => setItems((prev) => {
      const next = collect();
      return sameItems(prev, next) ? prev : next;
    });
    update();
    let frame = 0;
    const observer = new MutationObserver(() => {
      if (!frame) frame = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    });
    const root = document.querySelector('article') ?? document.body;
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <ListingStickyBar
      listing={listing}
      anchorId={anchorId}
      anchors={items.length > 1 ? { items, label: t('listingDetail.anchors.label', { defaultValue: 'On this page' }) } : undefined}
    />
  );
}
